import { ChangeDetectorRef, Directive, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, inject } from '@angular/core';
import { NgForm } from '@angular/forms';
import { CheckoutFormComponentDef } from '@df/checkout/checkout-form.component.def';
import { CheckoutStepComponentDef } from '@df/checkout/checkout-step.component.def';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { App } from '@df/core/app';
import { Status } from '@df/core/status';
import isFunction from 'lodash-es/isFunction';
import minBy from 'lodash-es/minBy';
import { Subscription } from 'rxjs';
import { NCheckout } from './df-checkout.config';

@Directive()
export abstract class CheckoutShippingMethodsComponentDef implements OnInit, OnDestroy, Checkout.ICheckoutShippingMethodsComponent {
    protected moduleConfig = inject(NCheckout.OPTIONS);

    /**
     * Step form  (check also ngOnDestroy)
     * important: you need to put #stepForm="ngForm" on form element in template
     */
    protected _stepForm?: NgForm;
    @ViewChild('stepForm', { static: false })
    set stepForm(form: NgForm) {
        this.onStepFormSet(form);
    }

    /**
     * Override for standard shipping data object
     */
    protected _data?: Checkout.IQuoteDataShipping;

    /**
     * Use data input to override standard shipping slot
     */
    @Input()
    set data(data: Checkout.IQuoteDataShipping) {
        this._data = data;
        this.onCheckoutChange({ full: true });
    }

    /**
     * Shipping method data shorthand
     */
    get data(): Checkout.IQuoteDataShipping {
        return this._data || this.checkoutFormComponent.data.shipping;
    }

    @Output()
    dataChange = new EventEmitter<Checkout.IQuoteDataShipping>();

    /**
     * Checkout changes subscription
     */
    protected checkoutSubscription?: Subscription;

    @Input()
    _methods?: Checkout.IShippingMethod[];

    /**
     * Class constructor
     */
    constructor(
        protected checkoutFormComponent: CheckoutFormComponentDef,
        protected checkoutStepComponent: CheckoutStepComponentDef,
        private app: App,
        private changeDetectorRef: ChangeDetectorRef
    ) {}

    /////////////////////////////////////////////
    //
    // EVENTS
    //
    /////////////////////////////////////////////

    /**
     * Set up component
     */
    ngOnInit() {
        this.checkoutSubscription = this.checkoutFormComponent.subscribe((change: Checkout.IStateChange) => this.onCheckoutChange(change));
        this.onCheckoutChange({ full: true });
    }

    /**
     * Cleanup
     */
    ngOnDestroy() {
        this.checkoutSubscription?.unsubscribe();
        this.checkoutStepComponent.removeForm(this._stepForm);
    }

    protected onStepFormSet(form: NgForm): void {
        this.checkoutStepComponent.removeForm(this._stepForm);
        this.checkoutStepComponent.addForm(form);
        this._stepForm = form;
    }

    /**
     * copies customer shipping methods to local variable
     */
    protected onCheckoutChange(change: Checkout.IStateChange) {
        if (this.data.method) {
            // if method is no longer allowed then unset it
            if (!this.methods.find(i => i.code === this.data.method)) {
                this.setMethod(undefined);
            }
        }
        
        this.pickFirst();
        this.detectChanges();
    }

    /////////////////////////////////////////////
    //
    // COMPONENT DATA
    //
    /////////////////////////////////////////////

    /**
     * Update status shorthand
     */
    get status(): Status {
        return this.checkoutFormComponent.saveShippingAddressStatus;
    }

    /**
     * Returns true if shipping method is selection not needed
     * @return {boolean}
     */
    get noShippingNeeded(): boolean {
        return this.checkoutFormComponent.isVirtual;
    }

    /**
     * All shipping methods shorthand
     */
    get allMethods(): Checkout.IShippingMethod[] {
        return this.checkoutFormComponent.shippingMethods;
    }

    /**
     * Shipping methods of collection type shorthand
     */
    get collectionMethods(): Checkout.IShippingMethod[] {
        return this.checkoutFormComponent.collectionShippingMethods;
    }

    /**
     * All shipping methods of non collection type shorthand
     */
    get methods(): Checkout.IShippingMethod[] {
        return this._methods || this.checkoutFormComponent.nonCollectionShippingMethods;
    }

    /**
     * Retrieves method instance
     */
    getMethod(code: string | undefined = this.data.method): Checkout.IShippingMethod | undefined {
        if (!code) {
            return undefined;
        }
        if (this._methods) {
            return this._methods.find(i => i.code === code);
        }
        return this.checkoutFormComponent.shippingMethodByCode(code);
    }

    private setMethod(method: string | undefined) {
        this.data.method = method;
        this.dataChange.emit(this.data);
        if (method) {
            this.onMethodPick();
        }
    }

    /////////////////////////////////////////////
    //
    // PROCESSING
    //
    /////////////////////////////////////////////

    /**
     * Picks first non pickup method if checkoutConfig allows it
     */
    protected pickFirst() {
        if (this.data) {
            let methods: Checkout.IShippingMethod[] | undefined;

            if (!this.data.method) {
                // autoPickShippingMethodIfOnlyOneAvailable
                if (!methods && this.moduleConfig.autoPickShippingMethodIfOnlyOneAvailable && this.methods.length === 1) {
                    this.setMethod(this.methods[0].code);
                } else {
                    // autoPickShippingMethod
                    const autoPick = this.moduleConfig.autoPickShippingMethod;
                    if (autoPick) {
                        if (isFunction(autoPick)) {
                            methods = autoPick(this);
                        } else if (autoPick === true) {
                            methods = this.methods;
                        } else if (Array.isArray(autoPick)) {
                            methods = this.methods.filter(method => autoPick.includes(method.code));
                        }
                    }

                    // autoPickCheepestShippingMethod
                    if (this.moduleConfig.autoPickCheepestShippingMethod) {
                        if (!methods) {
                            methods = this.methods;
                        }
                        methods = methods ? [minBy(methods, i => i.price) as Checkout.IShippingMethod] : undefined;
                    }

                    if (methods && methods.length) {
                        this.setMethod(methods[0]?.code);
                    }
                    // make sure that method is undefined (otherwise our watch wont work as it skips change from undefiend to some value)
                    if (!this.data.method) {
                        this.setMethod(undefined);
                    }
                }
            } else {
                this.setMethod(this.data.method);
            }
        }
    }

    protected onMethodPick(): void {
        const shippingMethod = this.data.method ? this.checkoutFormComponent.shippingMethodByCode(this.data.method) : undefined;
        const eventData: Checkout.ISaveShippingMethodEventData = { ...this.data, methodDetails: shippingMethod };

        this.app.events.broadcast<Checkout.ISaveShippingMethodEventData>(Checkout.EVENTS.PickShippingMethod, eventData);
    }

    get message(): string | undefined {
        return this.checkoutFormComponent.backendData?.shipping.message;
    }

    methodTrackFn(index, method: Checkout.IShippingMethod) {
        return method.code;
    }

    /**
     * use this getter to disbale shipping method selection
     * shipping method changing should not be allowed while shipping address or shipping method are currently saving
     */
    get disabled(): boolean {
        return this.status.busy || this.checkoutFormComponent.saveShippingMethodStatus.busy;
    }

    protected detectChanges() {
        this.changeDetectorRef.detectChanges();
    }
}
